import type { SimulationWrapperType } from "@/test-set/simulation-wrappers"

import { type SimulationInputViewMode } from "@/lib/viewModes"

import type { ToggleableColumn } from "../components/test-case-list/TestCaseColumnVisibility"
import type {
  EditableTestCase,
  EditableTestCaseRecordLike,
  HistoryEntry,
  PendingChanges,
  TestSetContext,
  TestSetMetadata,
} from "../model"
import {
  getInitialSimulationInputViewMode,
  hasKnowledgeBaseEntries,
  normalizeEditableTestCases,
} from "./testSetEditorState"
import { getPendingChangesAfterDelete } from "./testSetPendingChangeState"
import { getTestSetSimulationWrapperConfig } from "./testSetSimulationWrapperConfig"

interface TestSetMetadataRecordLike {
  created_at?: string
  created_by_id?: string | null
  description?: string | null
  display_name: string
  last_updated_at?: string
  test_set_metadata?: {
    simulationWrapperType?: SimulationWrapperType | null
  } | null
}

interface HydratedMetadataLike {
  createdAt?: string
  createdByUserId?: string | null
  description?: string
  displayName: string
  lastUpdatedAt?: string
  simulationWrapperType?: SimulationWrapperType | null
  simulationWrapperTopLevelConfig?: TestSetMetadata["simulationWrapperTopLevelConfig"]
}

interface AssociatedAgentLike {
  active?: boolean | null
  id: string
  test_set_ids?: string[] | null
}

interface InitialEditorStateInput {
  agentsById: Record<string, AssociatedAgentLike>
  isNewUnsaved: boolean
  metadata: TestSetMetadataRecordLike
  storedSelectedAgentIds?: string[] | null
  testCaseRecords: EditableTestCaseRecordLike[]
  testSetId: string
}

interface TestCaseSelectionState {
  selectedTestCaseId: string | null
  selectedTestCaseIds: Set<string>
  testCases: EditableTestCase[]
}

interface ToggleSelectionInput {
  index: number
  lastToggledIndex: number | null
  selectedTestCaseIds: Set<string>
  shiftKey: boolean
  testCaseId: string
  testCases: EditableTestCase[]
}

function cloneEditableTestCases(
  testCases: EditableTestCase[],
): EditableTestCase[] {
  return JSON.parse(JSON.stringify(testCases)) as EditableTestCase[]
}

export {
  getAcceptedPendingAdditionState,
  getAcceptedPendingDeletionState,
  getAcceptedPendingEditState,
  getRejectedPendingAdditionState,
  getRejectedPendingDeletionState,
  getRejectedPendingEditState,
} from "./testSetPendingChangeState"

export function getLatestUpdatedAt(
  lastUpdatedAt: string | undefined,
  testCaseRecords: Pick<EditableTestCaseRecordLike, "last_updated_at">[],
): string | undefined {
  return testCaseRecords.reduce(
    (latest, record) =>
      record.last_updated_at &&
      (!latest || new Date(record.last_updated_at) > new Date(latest))
        ? record.last_updated_at
        : latest,
    lastUpdatedAt,
  )
}

export function buildTestSetMetadata(
  metadata: TestSetMetadataRecordLike,
  testCaseRecords: EditableTestCaseRecordLike[],
): TestSetMetadata {
  const testSetMetadata = metadata.test_set_metadata || {}
  const simulationWrapperType =
    (testSetMetadata.simulationWrapperType as SimulationWrapperType | null) ||
    null

  return {
    displayName: metadata.display_name,
    description: metadata.description || "",
    createdAt: metadata.created_at,
    lastUpdatedAt: getLatestUpdatedAt(
      metadata.last_updated_at,
      testCaseRecords,
    ),
    createdByUserId: metadata.created_by_id,
    simulationWrapperType,
    simulationWrapperTopLevelConfig: getTestSetSimulationWrapperConfig(
      simulationWrapperType,
      testCaseRecords,
    ),
  }
}

export function buildHydratedTestSetMetadata(
  metadata: HydratedMetadataLike,
  testCases: EditableTestCase[],
): TestSetMetadata {
  const simulationWrapperType = metadata.simulationWrapperType || null

  return {
    displayName: metadata.displayName,
    description: metadata.description || "",
    createdAt: metadata.createdAt,
    lastUpdatedAt: metadata.lastUpdatedAt,
    createdByUserId: metadata.createdByUserId,
    simulationWrapperType,
    simulationWrapperTopLevelConfig:
      metadata.simulationWrapperTopLevelConfig ??
      getTestSetSimulationWrapperConfig(simulationWrapperType, testCases),
  }
}

export function getAssociatedAgentIds(
  agentsById: Record<string, AssociatedAgentLike>,
  testSetId: string,
): string[] {
  return Object.values(agentsById)
    .filter((agent) => agent.test_set_ids?.includes(testSetId))
    .map((agent) => agent.id)
}

export function getColumnsToShowForTestCases(
  testCases: EditableTestCase[],
): ToggleableColumn[] {
  const columns: ToggleableColumn[] = []

  if (hasKnowledgeBaseEntries(testCases)) {
    columns.push("kbEntries")
  }

  if (testCases.some((tc) => tc.description)) {
    columns.push("description")
  }

  if (testCases.some((tc) => tc.expected_behaviors.length > 0)) {
    columns.push("behaviors")
  }

  return columns
}

export function getInitialEditorState({
  agentsById,
  isNewUnsaved,
  metadata,
  storedSelectedAgentIds,
  testCaseRecords,
  testSetId,
}: InitialEditorStateInput) {
  const testSetMetadata = buildTestSetMetadata(metadata, testCaseRecords)
  const editableTestCases = normalizeEditableTestCases(testCaseRecords)
  const selectedAgentIds = isNewUnsaved
    ? [...(storedSelectedAgentIds || [])]
    : getAssociatedAgentIds(agentsById, testSetId)
  const activeAgentId = selectedAgentIds[0] || ""
  const columnsToShow = getColumnsToShowForTestCases(editableTestCases)

  return {
    activeAgentId,
    columnsToShow,
    initialActiveAgentId: activeAgentId,
    initialMetadata: testSetMetadata,
    initialSelectedAgentIds: selectedAgentIds,
    initialTestCases: cloneEditableTestCases(editableTestCases),
    metadata: testSetMetadata,
    selectedAgentIds,
    simulationInputViewMode: getInitialSimulationInputViewMode(
      editableTestCases,
    ) as SimulationInputViewMode,
    testCases: editableTestCases,
  }
}

export function createHistoryEntry(
  actionType: HistoryEntry["actionType"],
  description: string,
  testCases: EditableTestCase[],
  selectedTestCaseId: string | null,
  timestamp = Date.now(),
): HistoryEntry {
  return {
    actionType,
    description,
    selectedTestCaseId,
    testCases: cloneEditableTestCases(testCases),
    timestamp,
  }
}

export function appendHistoryEntry(
  history: HistoryEntry[],
  entry: HistoryEntry,
  maxHistory: number,
): HistoryEntry[] {
  const nextHistory = [...history, entry]
  if (nextHistory.length <= maxHistory) {
    return nextHistory
  }
  return nextHistory.slice(-maxHistory)
}

export function getUpdatedMetadata(
  metadata: TestSetMetadata,
  updates: {
    description?: string
    displayName?: string
    simulationWrapperType?: SimulationWrapperType | null
    simulationWrapperTopLevelConfig?: TestSetMetadata["simulationWrapperTopLevelConfig"]
  },
): TestSetMetadata {
  return {
    ...metadata,
    ...(updates.displayName !== undefined && {
      displayName: updates.displayName,
    }),
    ...(updates.description !== undefined && {
      description: updates.description,
    }),
    ...(updates.simulationWrapperType !== undefined && {
      simulationWrapperType: updates.simulationWrapperType,
    }),
    ...(updates.simulationWrapperTopLevelConfig !== undefined && {
      simulationWrapperTopLevelConfig: updates.simulationWrapperTopLevelConfig,
    }),
  }
}

export function getDeletedTestCaseState({
  pendingChanges,
  selectedTestCaseId,
  selectedTestCaseIds,
  testCaseIds,
  testCases,
}: TestCaseSelectionState & {
  pendingChanges: PendingChanges | null
  testCaseIds: string[]
}) {
  const idsToDelete = new Set(testCaseIds)
  const nextTestCases = testCases.filter(
    (testCase) => !idsToDelete.has(testCase.id),
  )
  const nextSelectedTestCaseIds = new Set(selectedTestCaseIds)

  for (const testCaseId of idsToDelete) {
    nextSelectedTestCaseIds.delete(testCaseId)
  }

  return {
    pendingChanges: getPendingChangesAfterDelete(pendingChanges, idsToDelete),
    selectedTestCaseId:
      selectedTestCaseId && idsToDelete.has(selectedTestCaseId)
        ? (nextTestCases[0]?.id ?? null)
        : selectedTestCaseId,
    selectedTestCaseIds:
      nextSelectedTestCaseIds.size === selectedTestCaseIds.size
        ? selectedTestCaseIds
        : nextSelectedTestCaseIds,
    testCases: nextTestCases,
  }
}

export function getCopiedTestCase(
  testCases: EditableTestCase[],
  testCaseId: string,
  nextId: string,
): EditableTestCase | null {
  const testCase = testCases.find(
    (currentTestCase) => currentTestCase.id === testCaseId,
  )

  if (!testCase) {
    return null
  }

  return {
    ...testCase,
    id: nextId,
  }
}

export function getHydratedEditorState(input: {
  activeAgentId: string
  metadata: HydratedMetadataLike
  selectedAgentIds: string[]
  testCases: EditableTestCase[]
}) {
  const metadata = buildHydratedTestSetMetadata(input.metadata, input.testCases)
  const testCases = cloneEditableTestCases(input.testCases)

  return {
    activeAgentId: input.activeAgentId,
    history: [] as HistoryEntry[],
    initialActiveAgentId: input.activeAgentId,
    initialMetadata: metadata,
    initialSelectedAgentIds: [...input.selectedAgentIds],
    initialTestCases: cloneEditableTestCases(input.testCases),
    metadata,
    pendingChanges: null as PendingChanges | null,
    selectedAgentIds: [...input.selectedAgentIds],
    selectedTestCaseId: testCases[0]?.id || null,
    testCases,
  }
}

export function getSavedEditorState(input: {
  activeAgentId: string
  metadata: TestSetMetadata
  selectedAgentIds: string[]
  testCases: EditableTestCase[]
}) {
  return {
    history: [] as HistoryEntry[],
    initialActiveAgentId: input.activeAgentId,
    initialMetadata: { ...input.metadata },
    initialSelectedAgentIds: [...input.selectedAgentIds],
    initialTestCases: cloneEditableTestCases(input.testCases),
    pendingChanges: null as PendingChanges | null,
  }
}

export function getResetEditorState(input: {
  initialActiveAgentId: string
  initialMetadata: TestSetMetadata
  initialSelectedAgentIds: string[]
  initialTestCases: EditableTestCase[]
}) {
  const testCases = cloneEditableTestCases(input.initialTestCases)

  return {
    activeAgentId: input.initialActiveAgentId,
    history: [] as HistoryEntry[],
    metadata: { ...input.initialMetadata },
    pendingChanges: null as PendingChanges | null,
    selectedAgentIds: [...input.initialSelectedAgentIds],
    selectedTestCaseId: testCases[0]?.id || null,
    testCases,
  }
}

export function getUndoState(history: HistoryEntry[]) {
  const lastEntry = history[history.length - 1]
  if (!lastEntry) {
    return null
  }

  return {
    history: history.slice(0, -1),
    selectedTestCaseId: lastEntry.selectedTestCaseId,
    testCases: lastEntry.testCases,
  }
}

export function getRevertedHistoryState(
  history: HistoryEntry[],
  historyIndex: number,
) {
  const targetEntry = history[historyIndex]
  if (!targetEntry) {
    return null
  }

  return {
    history: history.slice(0, historyIndex),
    selectedTestCaseId: targetEntry.selectedTestCaseId,
    testCases: targetEntry.testCases,
  }
}

export function getToggledSelectionState({
  index,
  lastToggledIndex,
  selectedTestCaseIds,
  shiftKey,
  testCaseId,
  testCases,
}: ToggleSelectionInput) {
  if (shiftKey && lastToggledIndex !== null) {
    const start = Math.min(lastToggledIndex, index)
    const end = Math.max(lastToggledIndex, index)
    const nextSelectedTestCaseIds = new Set<string>()

    for (let currentIndex = start; currentIndex <= end; currentIndex++) {
      if (testCases[currentIndex]) {
        nextSelectedTestCaseIds.add(testCases[currentIndex].id)
      }
    }

    return {
      lastToggledIndex: index,
      selectedTestCaseIds: nextSelectedTestCaseIds,
    }
  }

  const nextSelectedTestCaseIds = new Set(selectedTestCaseIds)
  if (nextSelectedTestCaseIds.has(testCaseId)) {
    nextSelectedTestCaseIds.delete(testCaseId)
  } else {
    nextSelectedTestCaseIds.add(testCaseId)
  }

  return {
    lastToggledIndex: index,
    selectedTestCaseIds: nextSelectedTestCaseIds,
  }
}

export function getSelectAllState(testCases: EditableTestCase[]) {
  return {
    lastToggledIndex: testCases.length - 1,
    selectedTestCaseIds: new Set(testCases.map((testCase) => testCase.id)),
  }
}

export function buildTestSetContext(
  description?: string,
  agent?: {
    display_name: string
    model_type?: string
    prompt?: string | null
    workflows?: Record<string, unknown>
    knowledge_base?: { name: string; description?: string }[]
  },
): TestSetContext | null {
  if (!description && !agent) {
    return null
  }

  return {
    description: description || "",
    ...(agent && {
      agent: {
        display_name: agent.display_name,
        model_type: agent.model_type,
        prompt: agent.prompt,
        workflows: agent.workflows,
        knowledge_base: agent.knowledge_base,
      },
    }),
  }
}
